import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Typography } from '@mui/material';
import { Image, PaintBrush, Trash } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { UploadService } from '../../../../service/UploadService';
import {
  INode,
  ITypeNode,
  useFormBuilder,
} from '../../Context/FormBuilderContext';
import { ActionTypes } from '../../Reducer';
import { CustomizationForm } from '../FormCustomization/CustomizationForm';

interface ImageSettingsProps {
  node: INode;
}

interface UploadedFile {
  id: string;
  file: File;
  name: string;
  progress: number;
  status: 'uploading' | 'complete' | 'error';
  error?: string;
  fileUrl?: string;
  response?: any;
  isExtraction: boolean;
}

const ImageSchema = z.object({
  url: z.string().optional(),
  fontFamily: z.string().default('Roboto, sans-serif'),
  fontSize: z.number().min(8).max(72).default(16),
  isBold: z.boolean().default(false),
  isItalic: z.boolean().default(false),
  isUnderlined: z.boolean().default(false),
  textAlign: z.enum(['left', 'center', 'right', 'justify']).default('left'),
  titleColor: z.string().default('#1F2937'),
  subtitleFontFamily: z.string().default('Roboto, sans-serif'),
  subtitleFontSize: z.number().min(8).max(72).default(16),
  isSubtitleBold: z.boolean().default(false),
  isSubtitleItalic: z.boolean().default(false),
  isSubtitleUnderlined: z.boolean().default(false),
  subtitleTextAlign: z
    .enum(['left', 'center', 'right', 'justify'])
    .default('left'),
  subtitleColor: z.string().default('#1F2937'),
  backgroundColor: z.string().default('#ffffff'),
  borderColor: z.string().default('#ccc'),
  borderRadius: z.number().min(4).max(72).default(4),
  borderWeight: z.number().min(1).max(10).default(1),
  verticalAlign: z.enum(['top', 'center', 'bottom']).default('top'),
  padding: z.number().min(0).max(72).default(16),
});

export function ImageSettings({ node }: ImageSettingsProps) {
  const { dispatch } = useFormBuilder();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const styles = node?.data?.styles;

  const formsMethods = useForm({
    defaultValues: {
      url: node?.data?.url || '',
      fontFamily: styles?.title?.fontFamily || 'Roboto, sans-serif',
      fontSize: styles?.title?.fontSize || 16,
      isBold: styles?.title?.isBold || false,
      isItalic: styles?.title?.isItalic || false,
      isUnderlined: styles?.title?.isUnderlined || false,
      textAlign: styles?.title?.textAlign || 'left',
      titleColor: styles?.title?.color || '#1F2937',
      subtitleFontFamily: styles?.subtitle?.fontFamily || 'Roboto, sans-serif',
      subtitleFontSize: styles?.subtitle?.fontSize || 16,
      isSubtitleBold: styles?.subtitle?.isBold || false,
      isSubtitleItalic: styles?.subtitle?.isItalic || false,
      isSubtitleUnderlined: styles?.subtitle?.isUnderlined || false,
      subtitleTextAlign: styles?.subtitle?.textAlign || 'left',
      subtitleColor: styles?.subtitle?.color || '#1F2937',
      backgroundColor: styles?.backgroundColor || '#ffffff',
      borderColor: styles?.borderColor || '#3650DE',
      borderRadius: styles?.borderRadius || 4,
      borderWeight: styles?.borderWeight || 1,
      verticalAlign: styles?.verticalAlign || 'top',
      padding: styles?.padding !== undefined ? styles.padding : 16,
    },
    resolver: zodResolver(ImageSchema),
  });

  const onDrop = async (
    acceptedFiles: File[],
    rejectedFiles: FileRejection[],
  ) => {
    const newFiles = acceptedFiles.map(file => ({
      id: `${file.name}-${Date.now()}`,
      file,
      name: file.name,
      progress: 0,
      status: 'uploading' as const,
      isExtraction: false,
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);
    for (const fileData of newFiles) {
      await uploadFile(fileData);
    }
  };

  const uploadFile = async (fileData: UploadedFile) => {
    try {
      const formData = new FormData();
      formData.append('file', fileData.file);

      const progressInterval = setInterval(() => {
        setUploadedFiles(prev =>
          prev.map(f => {
            if (f.id === fileData.id && f.progress < 90) {
              return { ...f, progress: f.progress + 10 };
            }
            return f;
          }),
        );
      }, 500);

      const response = await UploadService.uploadFile(formData);

      clearInterval(progressInterval);

      setUploadedFiles(prev =>
        prev.map(f => {
          if (f.id === fileData.id) {
            return {
              ...f,
              status: 'complete' as const,
              progress: 100,
              fileUrl: response.data.src,
              response: response,
            };
          }
          return f;
        }),
      );

      if (response.data.src) {
        formsMethods.setValue('url', response.data.src);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadedFiles(prev =>
        prev.map(f => {
          if (f.id === fileData.id) {
            return {
              ...f,
              status: 'error' as const,
              error: error instanceof Error ? error.message : 'Upload failed',
            };
          }
          return f;
        }),
      );
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/jpg': ['.jpg'],
    },
    onDrop,
  });

  const handleRemoveImage = () => {
    formsMethods.setValue('url', '');
    setUploadedFiles([]);
  };

  const handleFormChange = (data: any) => {
    const updatedNode = {
      ...node,
      data: {
        ...node.data,
        title: data.title,
        subtitle: data.subtitle,
        url: data.url,
        styles: {
          title: {
            fontFamily: data.fontFamily,
            fontSize: data.fontSize,
            isBold: data.isBold,
            isItalic: data.isItalic,
            isUnderlined: data.isUnderlined,
            textAlign: data.textAlign,
            color: data.titleColor,
          },
          subtitle: {
            fontFamily: data.subtitleFontFamily,
            fontSize: data.subtitleFontSize,
            isBold: data.isSubtitleBold,
            isItalic: data.isSubtitleItalic,
            isUnderlined: data.isSubtitleUnderlined,
            textAlign: data.subtitleTextAlign,
            color: data.subtitleColor,
          },
          backgroundColor: data.backgroundColor,
          borderColor: data.borderColor,
          borderRadius: data.borderRadius,
          borderWeight: data.borderWeight,
          verticalAlign: data.verticalAlign,
          padding: data.padding,
        },
      },
    };

    dispatch({
      type: ActionTypes.UPDATE_NODES_DATA,
      payload: {
        id: node.id,
        updates: { data: updatedNode.data },
      },
    });
  };

  useEffect(() => {
    const subscription = formsMethods.watch(data => {
      if (data && Object.keys(data).length > 0) {
        handleFormChange(data);
      }
    });

    return () => subscription.unsubscribe();
  }, [formsMethods.watch, node.id]);

  const formValues = formsMethods.watch();

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '16px 16px 0 16px',
        }}
      >
        <PaintBrush size={16} color="#101828" />
        <Typography
          style={{ fontSize: '14px', fontWeight: '500', color: '#101828' }}
        >
          Image Settings
        </Typography>
      </Box>

      <FormProvider {...formsMethods}>
        <form style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box
            sx={{
              display: 'flex',
              gap: '4px',
              padding: '0px 16px 0 16px',
            }}
          >
            <Box
              onClick={() => setActiveTab(0)}
              sx={{
                px: 2,
                py: 1,
                cursor: 'pointer',
                color: activeTab === 0 ? '#4F46E5' : '#6B7280',
                backgroundColor: activeTab === 0 ? '#F3F4F6' : 'transparent',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: activeTab === 0 ? '#F3F4F6' : '#F9FAFB',
                },
              }}
            >
              Content
            </Box>

            <Box
              onClick={() => setActiveTab(1)}
              sx={{
                px: 2,
                py: 1,
                cursor: 'pointer',
                color: activeTab === 1 ? '#4F46E5' : '#6B7280',
                backgroundColor: activeTab === 1 ? '#F3F4F6' : 'transparent',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: activeTab === 1 ? '#F3F4F6' : '#F9FAFB',
                },
              }}
            >
              Design
            </Box>
          </Box>

          <div
            style={{ width: '100%', height: '1px', backgroundColor: '#EAECF0' }}
          />

          {activeTab === 0 && (
            <Box sx={{ padding: '0px 16px 0 16px' }}>
              {!formValues?.url ? (
                <Controller
                  name="url"
                  control={formsMethods.control}
                  render={({ field, fieldState }) => (
                    <div
                      style={{
                        border: '1px dashed #667085',
                        borderRadius: '4px',
                        display: 'flex',
                        justifyContent: 'start',
                        alignItems: 'center',
                        color: '#98A2B3',
                        width: '100%',
                        minHeight: '120px',
                      }}
                    >
                      <div
                        {...getRootProps({ className: 'dropzone' })}
                        style={{
                          width: '100%',
                          height: '100%',
                          padding: '16px',
                          textAlign: 'center',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          color: '#475467',
                        }}
                      >
                        <input {...getInputProps()} />
                        <Image size={32} style={{ marginBottom: '8px' }} />
                        <p style={{ fontSize: '12px', margin: 0 }}>
                          Drag 'n' drop image here, or click to select
                        </p>
                        <p style={{ fontSize: '10px', marginTop: '4px' }}>
                          JPG, JPEG, PNG
                        </p>
                      </div>
                    </div>
                  )}
                />
              ) : (
                <Box
                  sx={{
                    position: 'relative',
                    textAlign: 'center',
                    width: '100%',
                    minHeight: '120px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src={formValues.url}
                    alt="Uploaded image"
                    style={{
                      width: '100%',
                      maxHeight: '120px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      border: '1px solid #E4E7EC',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                      },
                    }}
                    onClick={handleRemoveImage}
                  >
                    <Trash size={12} color="white" />
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ padding: '0px 16px 0 16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <CustomizationForm
                control={formsMethods.control}
                type={ITypeNode.IMAGE}
              />
            </Box>
          )}
        </form>
      </FormProvider>
    </>
  );
}
